import { DateTime } from 'luxon';
import {
  SaveActivityFetchSessionVariables,
  SaveActivityFetchVariables,
} from '../activity-form-v2/activity-form-actions-v2';
import { AddOneOffSessionData } from '../../rosters/common/AddOneOffSessionDialog';
import {
  AUTO_REPORT_VALUE,
  ImportActivityType,
  ONE_OFF_TYPES,
  oneOffKey,
  sessionKey,
  WEEKDAY_CODES,
} from './activity-import-columns';
import { ImportActivityRow, ImportLookups } from './activity-import-types';
import {
  getOneOffNumbers,
  getSessionNumbers,
  normaliseKey,
  parseActivityType,
  parseBool,
  parseDate,
  parseDateList,
  parseFrequency,
  parseInteger,
  parseTime,
  splitList,
} from './activity-import-validation';

type ImportSaveActivityVariables = SaveActivityFetchVariables & {
  addressHidingExempt?: boolean | null;
  allowRosterComments?: boolean | null;
};

export type ImportOneOffSession = Omit<AddOneOffSessionData, 'activityId'>;

export interface ActivityImportPayload {
  activity: ImportSaveActivityVariables;
  rosterDates: string[];
  oneOffSessions: ImportOneOffSession[];
}

const TYPE_DEFAULTS: Record<ImportActivityType, { restricted: boolean; openRoster: boolean }> = {
  regular: { restricted: true, openRoster: false },
  'once-off': { restricted: true, openRoster: false },
  flexible: { restricted: true, openRoster: false },
  event: { restricted: false, openRoster: true },
  'self-directed': { restricted: false, openRoster: true },
};

export const toDraftJs = (text?: string): string =>
  JSON.stringify({
    blocks: (text ?? '').split(/\r?\n/).map((line) => ({
      text: line,
      type: 'unstyled',
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {},
    })),
    entityMap: {},
  });

const lookupIds = (names: string[], lookup: Map<string, string>) =>
  names.map((name) => lookup.get(normaliseKey(name))).filter((id): id is string => Boolean(id));

const encodeTime = (value?: string) => {
  const time = parseTime(value);
  return time ? time.toFormat('HH:mm:ss') : null;
};

const weekdayOf = (date: DateTime) => WEEKDAY_CODES[date.weekday - 1];

const buildRecurrence = (values: Record<string, string>) => {
  const startDate = parseDate(values.startDate) as DateTime;
  const endDate = parseDate(values.endDate);
  const interval = parseInteger(values.repeatInterval) || 1;
  const days = splitList(values.repeatDays).map((day) => day.toUpperCase().slice(0, 2));
  const base = { startDate: startDate.toISODate(), endDate: endDate ? endDate.toISODate() : null };

  switch (parseFrequency(values.frequency)) {
    case 'daily':
      return { ...base, __typename: 'DailyRecurrenceType', dayInterval: interval };
    case 'weekly':
      return {
        ...base,
        __typename: 'WeeklyRecurrenceType',
        days: days.length ? days : [weekdayOf(startDate)],
        weekInterval: interval,
      };
    case 'fortnightly':
      return {
        ...base,
        __typename: 'WeeklyRecurrenceType',
        days: days.length ? days : [weekdayOf(startDate)],
        weekInterval: 2,
      };
    case 'weekdays':
      return { ...base, __typename: 'WeeklyRecurrenceType', days: WEEKDAY_CODES.slice(0, 5), weekInterval: interval };
    case 'weekends':
      return { ...base, __typename: 'WeeklyRecurrenceType', days: WEEKDAY_CODES.slice(5), weekInterval: interval };
    case 'monthly-date':
      return {
        ...base,
        __typename: 'MonthlyByMonthDayRecurrenceType',
        monthDay: startDate.day,
        monthInterval: interval,
      };
    case 'monthly-nth-day':
      return {
        ...base,
        __typename: 'MonthlyNthDayRecurrenceType',
        n: Math.ceil(startDate.day / 7),
        day: weekdayOf(startDate),
        monthInterval: interval,
      };
    case 'yearly':
      return { ...base, __typename: 'YearlyRecurrenceType', yearInterval: interval };
    default:
      return undefined;
  }
};

const buildSessions = (
  values: Record<string, string>,
  activityType: ImportActivityType,
  lookups: ImportLookups
): SaveActivityFetchSessionVariables[] =>
  getSessionNumbers(values).map((n) => {
    const get = (suffix: string) => values[sessionKey(n, suffix)];
    const reportType = normaliseKey(get('_report_type') ?? '');
    const individualReports = normaliseKey(get('_individual_reports') ?? '');

    return {
      name: get(''),
      description: toDraftJs(get('_desc')),
      minVolunteers: parseInteger(get('_min')) ?? null,
      maxVolunteers: parseInteger(get('_max')) ?? null,
      startTime: encodeTime(get('_start')),
      endTime: encodeTime(get('_end')),
      reportTypeId: reportType && reportType !== AUTO_REPORT_VALUE ? lookups.reportTypes.get(reportType) ?? null : null,
      autoReport: reportType === AUTO_REPORT_VALUE,
      isFlexible: false,
      individualReports: individualReports || (activityType === 'self-directed' ? 'mandatory' : null),
      requiredRoleIds: lookupIds(splitList(get('_required_roles')), lookups.roles),
    } as SaveActivityFetchSessionVariables;
  });

const buildOneOffSessions = (
  values: Record<string, string>,
  activityType: ImportActivityType,
  lookups: ImportLookups
): ImportOneOffSession[] => {
  if (!ONE_OFF_TYPES.includes(activityType)) return [];

  return getOneOffNumbers(values).reduce((acc, n) => {
    const get = (suffix: string) => values[oneOffKey(n, suffix)];
    const reportType = normaliseKey(get('_report_type') ?? '');
    const session = {
      name: get(''),
      description: toDraftJs(get('_desc')),
      minVolunteers: parseInteger(get('_min')) ?? undefined,
      maxVolunteers: parseInteger(get('_max')) ?? undefined,
      startTime: parseTime(get('_start'))?.toFormat('HH:mm'),
      endTime: parseTime(get('_end'))?.toFormat('HH:mm'),
      reportTypeId: reportType && reportType !== AUTO_REPORT_VALUE ? lookups.reportTypes.get(reportType) : undefined,
      autoReport: reportType === AUTO_REPORT_VALUE,
    };
    const dates = parseDateList(get('_date')).dates.sort((a, b) => a.toMillis() - b.toMillis());
    return acc.concat(dates.map((date) => ({ ...session, activityDate: date.toISODate() })));
  }, [] as ImportOneOffSession[]);
};

export const buildActivityPayload = (row: ImportActivityRow, lookups: ImportLookups): ActivityImportPayload => {
  const { values } = row;
  const activityType = parseActivityType(values.activityType) as ImportActivityType;
  const defaults = TYPE_DEFAULTS[activityType];
  const today = DateTime.local().startOf('day');
  const timeZone = DateTime.local().zone.name;

  const bool = (key: string, fallback: boolean) => parseBool(values[key]) ?? fallback;
  const optionalBool = (key: string) => parseBool(values[key]) ?? null;
  const optionalInt = (key: string) => parseInteger(values[key]) ?? null;

  const hasDates = activityType === 'event' || activityType === 'flexible';
  const dates = hasDates ? parseDateList(values.dates).dates.sort((a, b) => a.toMillis() - b.toMillis()) : [];
  const rosterDates = dates.map((date) => date.toISODate());

  let startDate = today;
  let endDate: DateTime | null = null;
  if (activityType === 'regular' || activityType === 'once-off') {
    startDate = parseDate(values.startDate) as DateTime;
    endDate = activityType === 'regular' ? parseDate(values.endDate) : null;
  } else if (activityType === 'event') {
    [startDate] = dates;
    endDate = dates[dates.length - 1];
  } else if (activityType === 'flexible' && dates.length && dates[0] < today) {
    [startDate] = dates;
  }

  const recurrence =
    activityType === 'regular'
      ? buildRecurrence(values)
      : activityType === 'once-off'
      ? { __typename: 'SingleRecurrenceType', startDate: startDate.toISODate(), endDate: null }
      : undefined;

  const activityLocation = row.resolvedAddress ? { placesAddress: row.resolvedAddress, timeZone, comments: '' } : null;

  const activity: ImportSaveActivityVariables = {
    programId: lookups.programs.get(normaliseKey(values.program)) as string,
    name: values.activityName,
    description: JSON.stringify(toDraftJs(values.description)),
    startDate: startDate.toISODate(),
    endDate: endDate ? endDate.toISODate() : undefined,
    activityLocation,
    remoteLocation: !activityLocation && values.remoteDetails ? { details: values.remoteDetails } : undefined,
    recurrences: recurrence ? [recurrence as any] : undefined,
    sessions: buildSessions(values, activityType, lookups),
    activityTagIds: lookupIds(splitList(values.tags), lookups.tags),
    requiredRoleIds: lookupIds(splitList(values.requiredRoles), lookups.roles),
    isRestrictedActivity: bool('restricted', defaults.restricted),
    isHidden: bool('hidden', false),
    hasOpenRoster: bool('openRoster', defaults.openRoster),
    requireRosterTemplateApproval: bool('requireRosterApproval', false),
    maxTeam: optionalInt('maxTeam'),
    priority: bool('priority', false),
    allowCICO: optionalBool('allowCico'),
    allowIndividualShiftTimes: optionalBool('allowIndividualShiftTimes'),
    allowRosterComments: optionalBool('allowRosterComments'),
    addressRevealBeforeDays: optionalInt('addressRevealBeforeDays'),
    addressHideAfterDays: optionalInt('addressHideAfterDays'),
    addressHidingExempt: optionalBool('addressHidingExempt'),
    activityType: activityType === 'event' ? 'event' : activityType === 'self-directed' ? 'self_directed' : undefined,
    isEvent: activityType === 'event' || undefined,
    eventDates: activityType === 'event' ? rosterDates : undefined,
  } as ImportSaveActivityVariables;

  return {
    activity,
    rosterDates: activityType === 'flexible' ? rosterDates : [],
    oneOffSessions: buildOneOffSessions(values, activityType, lookups),
  };
};
